import { FetchSecretKeyResponseDto } from "@/data/dto/fetch-secret-key-reponse-dto";
import { FetchSecretKeyByCookieRequestPayload } from "@/data/payload/auth/fetch-secret-key-by-cookie-payload";
import { FetchSecretKeyByTokenRequestPayload } from "@/data/payload/auth/fetch-secret-key-by-token-request-payload";
import { URLList } from "@/data/url_list";
import { CryptoUtil } from "@/utils/crypto/crypto";
import { SessionKeyHolder } from "@/utils/crypto/session-key-holder";
import { dateToServerDate } from "@/utils/date-util";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATErrorModel, SATResult } from "@sadtx/sat-error";
import { TimeZoneCheckResponseDto } from "../dto/time-zone-check-dto";

/**
 * 인증, 암호화 관련 API 요청을 담당하는 데이터 소스 클래스
 */
export class AuthSource {
  constructor(
    private readonly client: FetchApiClientMethod,
    private readonly cryptoUtil: CryptoUtil,
    private readonly sessionKeyHolder: SessionKeyHolder,
  ) {}

  /**
   * 복호화된 AES 키에서 실제 키 값을 추출합니다.
   * @param aesKey - 복호화된 키 (문자열 또는 객체)
   * @returns 실제 키 값 (문자열)
   */
  private extractKeyValue(aesKey: string | object | null | undefined): string {
    if (!aesKey) {
      throw new SATErrorModel.FailToGetSecretKey("AES key is empty");
    }

    // 문자열인 경우 JSON 파싱 시도
    if (typeof aesKey === "string") {
      try {
        const parsed = JSON.parse(aesKey);
        if (typeof parsed === "object" && parsed !== null && "key" in parsed) {
          return parsed.key;
        }
        return aesKey; // 파싱 실패하거나 객체가 아니면 원본 반환
      } catch {
        return aesKey; // JSON 파싱 실패 시 원본 반환
      }
    }

    // 객체인 경우 key 속성 확인
    if (typeof aesKey === "object" && aesKey !== null && "key" in aesKey) {
      return (aesKey as { key: string }).key;
    }

    // 그 외의 경우 문자열로 변환
    return String(aesKey);
  }

  /**
   * 토큰을 통한 공개키 교환 및 대칭키 생성
   * @param token - 인증 토큰
   * @returns {Promise<SATResult<string, SATError>>} AES 대칭키
   */
  async exchangeSecretKeyByToken(token: string): Promise<SATResult<void, SATError>> {
    console.log("[AuthSource] :: exchangeSecretKeyByToken :: 비밀키 교환 요청 시작");

    try {
      // 1. RSA 키페어 생성
      const keyPair = await this.cryptoUtil.webGenerateRSAKeyPair();
      const pemPublicKey = await this.cryptoUtil.exportKeyToPem(keyPair.publicKey, "public");

      // 2. 서버에 공개키 전송 및 암호화된 대칭키 수신
      const pemPublicKeyPayload: FetchSecretKeyByTokenRequestPayload = {
        token: token,
        publicKey: pemPublicKey,
      };
      const encryptKeyResponseData = await this.fetchSecretKeyWithToken(pemPublicKeyPayload);

      if (encryptKeyResponseData.isFailure) {
        console.error(
          "[AuthSource] :: exchangeSecretKeyByToken :: 키 교환 실패",
          encryptKeyResponseData.error,
        );
        return SATResult.failure(encryptKeyResponseData.error);
      }

      // 3. RSA 개인키로 암호화된 대칭키 복호화
      const encryptData = encryptKeyResponseData.value.output;
      const aesKey = await this.cryptoUtil.webDecryptRSA(keyPair.privateKey, encryptData);

      const keyValue = this.extractKeyValue(aesKey);
      await this.sessionKeyHolder.setRawKey(keyValue);
      console.log("[AuthSource] :: exchangeSecretKeyByToken :: 비밀키 교환 완료");
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error("[AuthSource] :: exchangeSecretKeyByToken :: 키 교환 중 오류 발생", error);
      const satError = errorConverter.convertError(error);
      return SATResult.failure(satError);
    }
  }
  /**
   * 쿠키를 통한 공개키 교환 및 대칭키 생성
   * @returns {Promise<SATResult<string, SATError>>} AES 대칭키
   */
  async exchangeSecretKeyByCookie(): Promise<SATResult<void, SATError>> {
    console.log("[AuthSource] :: exchangeSecretKeyByCookie :: 비밀키 교환 요청 시작");

    try {
      // 1. RSA 키페어 생성
      const keyPair = await this.cryptoUtil.webGenerateRSAKeyPair();
      const pemPublicKey = await this.cryptoUtil.exportKeyToPem(keyPair.publicKey, "public");

      // 2. 서버에 공개키 전송 및 암호화된 대칭키 수신
      const pemPublicKeyPayload: FetchSecretKeyByCookieRequestPayload = {
        input: pemPublicKey,
        format: "raw",
      };
      const encryptKeyResponseData = await this.fetchSecretKeyWithCookie(pemPublicKeyPayload);

      if (encryptKeyResponseData.isFailure) {
        console.error(
          "[AuthSource] :: exchangeSecretKeyByCookie :: 키 교환 실패",
          encryptKeyResponseData.error,
        );
        return SATResult.failure(encryptKeyResponseData.error);
      }

      // 3. RSA 개인키로 감싼 대칭키 원본 바이트를 CryptoKey 로 바로 푼다 (키 원본이 JS 메모리를 거치지 않음)
      const encryptData = encryptKeyResponseData.value.output;
      const sessionKey = await this.cryptoUtil.webUnwrapAesKeyRSA(keyPair.privateKey, encryptData);

      this.sessionKeyHolder.setKey(sessionKey);
      console.log("[AuthSource] :: exchangeSecretKeyByCookie :: 비밀키 교환 완료");
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error("[AuthSource] :: exchangeSecretKeyByCookie :: 키 교환 중 오류 발생", error);
      const satError = errorConverter.convertError(error);
      return SATResult.failure(satError);
    }
  }

  /**
   * 사용자 토큰 체크
   * @returns {Promise<SATResult<void, SATError>>} 사용자 토큰 체크 결과
   */
  async userTokenCheck(): Promise<SATResult<void, SATError>> {
    try {
      await this.client.get(URLList.AUTH.USER_TOKEN_CHECK);
      console.log("[AuthSource] :: userTokenCheck :: 토큰 체크 요청 성공");
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error("[AuthSource] :: userTokenCheck :: 토큰 체크 요청 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AuthSource] :: userTokenCheck | ", satError);
      return SATResult.failure(satError);
    }
  }

  async checkTimeZone(): Promise<SATResult<TimeZoneCheckResponseDto, SATError>> {
    try {
      const date = dateToServerDate();
      const url = `${URLList.AUTH.TIME_ZONE_CHECK}/${date}`;
      const response = await this.client.get(url);
      console.log("[AuthSource] :: checkTimeZone :: 시간대 체크 요청 성공", response.data);
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[AuthSource] :: checkTimeZone :: 시간대 체크 요청 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AuthSource] :: checkTimeZone | ", satError);
      return SATResult.failure(satError);
    }
  }

  //MARK:- Private
  /**
   * 쿠키를 통한 비밀키 조회
   * @param payload - 비밀키 조회 요청 데이터, 공개키
   * @returns {Promise<SATResult<FetchSecretKeyResponseDto, SATError>>} 비밀키 조회 결과
   */
  private async fetchSecretKeyWithCookie(
    payload: FetchSecretKeyByCookieRequestPayload,
  ): Promise<SATResult<FetchSecretKeyResponseDto, SATError>> {
    try {
      console.log("[AuthSource] :: fetchSecretKey :: 서버에 공개키 전송 시작");
      const response = await this.client.post(
        URLList.AUTH.GET_SECRET_KEY_BY_COOKIE,
        payload,
      );
      console.log("[AuthSource] :: fetchSecretKey :: 서버 암호화된 대칭키 수신 완료");

      if (!response.data || !response.data.output) {
        throw new SATErrorModel.FailToGetSecretKey(
          "Failed to receive valid encryption key from server",
        );
      }

      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[AuthSource] :: fetchSecretKey :: 키 교환 요청 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AuthSource] :: fetchSecretKey | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 토큰을 통한 비밀키 조회
   * @param payload - 비밀키 조회 요청 데이터, 토큰, 공개키
   * @returns {Promise<SATResult<FetchSecretKeyResponseDto, SATError>>} 비밀키 조회 결과
   */
  private async fetchSecretKeyWithToken(
    payload: FetchSecretKeyByTokenRequestPayload,
  ): Promise<SATResult<FetchSecretKeyResponseDto, SATError>> {
    try {
      const response = await this.client.post(
        URLList.AUTH.GET_SECRET_KEY_BY_TOKEN,
        payload,
      );
      console.log("[AuthSource] :: fetchSecretKeyWithToken :: 서버 암호화된 대칭키 수신 완료");
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[AuthSource] :: fetchSecretKeyWithToken :: 키 교환 요청 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AuthSource] :: fetchSecretKeyWithToken | ", satError);
      return SATResult.failure(satError);
    }
  }
}
